"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Search, Download, Copy, Check, Blocks, Palette, Zap, Layout, Sun, Binary, Trophy, Cloud, Monitor, Box, LineChart, Tag, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { ThemePreviewPanel } from "@/components/ThemePreviewModal";

const LIME = "#ccfc45";

// Icons mapping
const Icons: Record<string, any> = {
    Palette, Zap, Layout, Sun, Binary, Trophy, Cloud, Monitor, Box, LineChart
};

// Map exact ids to Theme Colors for nice list accents
const ThemeColors: Record<string, string> = {
    "aurora-theme": "#10b981",    // emerald
    "crimson-theme": "#ef4444",   // red
    "matrix-void": "#84cc16",     // lime green
    "clean-slate": "#94a3b8",     // slate
    "golden-reserve": "#eab308",  // yellow/gold
    "violet-volt": "#8b5cf6",     // violet
    "skyline-minimal": "#3b82f6", // blue
    "retro-amber": "#f59e0b",     // amber
};

interface Extension {
    id: string;
    name: string;
    description: string;
    version: string;
    author: string;
    type: "theme" | "feature" | "tool";
    icon: string;
    downloadUrl: string;
}

export default function ExtensionsClient({ extensions }: { extensions: Extension[] }) {
    const [searchQuery, setSearchQuery] = useState("");
    const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
    const [mounted, setMounted] = useState(false);
    
    // Tab State
    const [activeTab, setActiveTab] = useState<"theme" | "feature">("theme");
    
    // Live Review State
    const [manualThemeId, setManualThemeId] = useState<string | null>(null);
    const [autoThemeId, setAutoThemeId] = useState<string>("clean-slate");

    useEffect(() => {
        setMounted(true);
        
        // Auto-cycle themes to look "alive" when nothing is manually clicked
        const themesList = extensions.filter(e => e.type === "theme");
        if (themesList.length > 0) {
            setAutoThemeId(themesList[0].id);
            let idx = 0;
            const cycleInterval = setInterval(() => {
                idx = (idx + 1) % themesList.length;
                setAutoThemeId(themesList[idx].id);
            }, 3000);
            
            return () => clearInterval(cycleInterval);
        }
    }, [extensions]);

    const handleCopy = (url: string) => {
        navigator.clipboard.writeText(url);
        setCopiedUrl(url);
        setTimeout(() => setCopiedUrl(null), 2000);
    };

    const filtered = extensions.filter(e => 
        e.type === activeTab &&
        (e.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
         e.description.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const currentPreviewTheme = manualThemeId || autoThemeId;

    return (
        <main className="h-screen w-screen overflow-hidden flex flex-col transition-colors duration-500 bg-background text-foreground p-4 md:p-6 pt-[80px] md:pt-[84px] pb-4 md:pb-6">
            {!mounted ? null : (
                <div className="w-full flex-1 rounded-[2.5rem] border bg-surface/50 border-border-subtle overflow-hidden relative shadow-[0_20px_60px_rgba(0,0,0,0.1)] flex flex-col md:flex-row">
                    
                    {/* ─────────── LEFT PANEL: Live Interactive Preview ─────────── */}
                    <div className="flex-1 flex flex-col h-full bg-black/5 relative overflow-hidden">
                        <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-background/30 to-transparent z-30 pointer-events-none flex items-center justify-between px-6 drop-shadow-md">
                            <span className="text-[10px] font-black uppercase tracking-[0.3em] opacity-50 flex items-center gap-2 mt-4 md:mt-0">
                                <Monitor size={12} /> Live Preview Rendering
                            </span>
                            {!manualThemeId && activeTab === "theme" && (
                                <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded border border-white/20 bg-black/20 text-white/60 animate-pulse mt-4 md:mt-0">
                                    AUTO-CYCLE ACTIVE
                                </span>
                            )}
                        </div>
                        
                        {/* If viewing themes, show the POS preview. Otherwise show a placeholder for features. */}
                        <AnimatePresence mode="wait">
                            {activeTab === "theme" ? (
                                <motion.div 
                                    key="theme-preview" 
                                    initial={{ opacity: 0, scale: 0.95 }} 
                                    animate={{ opacity: 1, scale: 1 }} 
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    className="w-full h-full flex items-center justify-center p-0 md:p-8 pt-14 md:pt-12 relative"
                                >
                                    {/* Ambient Background Glow matching the Theme */}
                                    <div 
                                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] blur-[100px] opacity-20 md:opacity-30 transition-colors duration-1000 pointer-events-none" 
                                        style={{ backgroundColor: ThemeColors[currentPreviewTheme] || LIME }} 
                                    />

                                    {/* Scaled Render Box with Glassmorphism Border */}
                                    <div className="w-[125%] h-[125%] origin-center shrink-0 relative z-10" style={{ transform: "scale(0.75) translateY(2%)" }}>
                                        <div className="w-full h-full p-1.5 md:p-2.5 rounded-[2rem] bg-white/10 backdrop-blur-3xl border border-white/10 shadow-[0_30px_100px_rgba(0,0,0,0.5),inset_0_0_0_1px_rgba(255,255,255,0.1)]">
                                            <div className="w-full h-full border border-white/10 overflow-hidden bg-[#0a0a0a] flex flex-col rounded-[1.5rem] shadow-inner ring-1 ring-black">
                                                <ThemePreviewPanel themeId={currentPreviewTheme} />
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div 
                                    key="feature-preview"
                                    initial={{ opacity: 0 }} 
                                    animate={{ opacity: 1 }} 
                                    exit={{ opacity: 0 }}
                                    className="w-full h-full flex flex-col items-center justify-center text-muted-foreground/50"
                                >
                                    <Blocks size={64} className="mb-4 opacity-20" />
                                    <p className="text-sm font-bold tracking-widest uppercase">Features apply logic, not UI.</p>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* ─────────── RIGHT PANEL: Browser & Tabs ─────────── */}
                    <div className="w-full md:w-[380px] lg:w-[420px] h-full flex flex-col shrink-0 border-l border-border-subtle bg-background/80 backdrop-blur-md relative z-20">
                        {/* Header Area */}
                        <div className="p-4 md:p-5 shrink-0 border-b border-border-subtle bg-surface">
                            <div className="flex items-center justify-between mb-3">
                                <h2 className="text-lg font-black tracking-tighter flex items-center gap-1.5">
                                    Modules <span style={{ color: LIME }}>Hub</span>
                                </h2>
                                
                                {/* Ultra-sleek Tab Switcher */}
                                <div className="flex p-0.5 bg-muted/60 rounded border border-black/5">
                                    <button 
                                        onClick={() => setActiveTab("theme")}
                                        className={cn("h-6 px-3 flex items-center justify-center gap-1.5 rounded-sm text-[9px] font-black uppercase tracking-widest transition-all", activeTab === "theme" ? "bg-background shadow-sm text-primary" : "text-muted-foreground hover:text-foreground")}
                                    >
                                        <Palette size={10} /> Themes
                                    </button>
                                    <button 
                                        onClick={() => setActiveTab("feature")}
                                        className={cn("h-6 px-3 flex items-center justify-center gap-1.5 rounded-sm text-[9px] font-black uppercase tracking-widest transition-all", activeTab === "feature" ? "bg-background shadow-sm text-primary" : "text-muted-foreground hover:text-foreground")}
                                    >
                                        <Sparkles size={10} /> Features
                                    </button>
                                </div>
                            </div>

                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                                <input
                                    type="text"
                                    placeholder={`Search ${activeTab}s...`}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full bg-background border border-border-subtle rounded py-2 pl-8 pr-3 text-[11px] font-bold focus:outline-none focus:border-primary/50 transition-all placeholder:text-muted-foreground/50 shadow-inner"
                                />
                            </div>
                        </div>

                        {/* List Area */}
                        <div className="flex-1 overflow-y-auto p-3 space-y-2 hide-scrollbar relative bg-black/5">
                            {filtered.length === 0 ? (
                                <div className="text-center py-20 opacity-40">
                                    <Box className="w-10 h-10 mx-auto mb-4" />
                                    <p className="text-[10px] font-black uppercase tracking-widest">No matching modules found.</p>
                                </div>
                            ) : (
                                filtered.map((ext, i) => {
                                    const accentColor = activeTab === "theme" ? (ThemeColors[ext.id] || LIME) : LIME;
                                    const Icon = Icons[ext.icon] || Blocks;
                                    const isActive = currentPreviewTheme === ext.id;
                                    
                                    return (
                                        <motion.div
                                            key={ext.id}
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: i * 0.03 }}
                                            onClick={() => setManualThemeId(ext.id)}
                                            className={cn(
                                                "group relative flex items-center gap-4 border rounded-2xl p-4 cursor-pointer transition-all duration-300",
                                                isActive ? "bg-surface shadow-[0_10px_30px_rgba(0,0,0,0.1)] ring-1 ring-offset-2 ring-primary/20" : "bg-surface/60 hover:bg-surface border-border-subtle hover:border-border"
                                            )}
                                        >
                                            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-1/2 rounded-r-full shadow-[0_0_15px_currentColor] opacity-0 transition-opacity" style={{ backgroundColor: accentColor, opacity: isActive ? 1 : 0 }} />
                                            
                                            <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0 border" style={{ backgroundColor: `${accentColor}10`, color: accentColor, borderColor: `${accentColor}20` }}>
                                                <Icon className="w-6 h-6" />
                                            </div>
                                            
                                            <div className="flex-1 min-w-0 pr-2">
                                                <div className="flex justify-between items-center mb-1">
                                                    <h3 className="text-[15px] font-black tracking-tight truncate transition-colors" style={{ color: isActive ? accentColor : 'inherit' }}>{ext.name}</h3>
                                                </div>
                                                <p className="text-[11px] text-muted-foreground font-medium leading-tight line-clamp-2">{ext.description}</p>
                                            </div>
                                            
                                            <div className="shrink-0 pl-2">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleCopy(ext.downloadUrl); }}
                                                    className={cn(
                                                        "w-10 h-10 rounded-full flex items-center justify-center border transition-all duration-300",
                                                        copiedUrl === ext.downloadUrl ? "bg-green-500/10 border-green-500/30" : "bg-background hover:bg-muted border-border-subtle"
                                                    )}
                                                >
                                                    {copiedUrl === ext.downloadUrl ? <Check className="w-4 h-4 text-green-500" /> : <Download className="w-4 h-4 text-foreground/80 group-hover:text-foreground" />}
                                                </button>
                                            </div>
                                        </motion.div>
                                    )
                                })
                            )}
                            <div className="h-20" />
                        </div>
                        {/* Overlay fade */}
                        <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent pointer-events-none" />
                    </div>
                </div>
            )}
        </main>
    );
}
